import React, { useEffect, useRef, useState } from "react";
import { useAuth, hasAuthParams } from "react-oidc-context";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useHistory } from "react-router-dom";
import { useSessionstorageState } from "rooks";
import LoginService from "services/loginService";
import { callbackPageStyles } from "./CallbackPageStyles";

const RETRY_COUNT_KEY = "callbackRetryCount";
const MAX_SIGNIN_RETRIES = 2;

function CallbackPage() {
  const auth = useAuth();
  const history = useHistory();
  const classes = callbackPageStyles();
  const [redirectPath, , removeRedirectPath] = useSessionstorageState("redirectPath", "/dashboard");
  const [signinFailed, setSigninFailed] = useState(false);
  const recoveryStartedRef = useRef(false);
  // this page also renders inside the hidden silent-renew iframe; never redirect from there
  const isInIframe = window.self !== window.top;

  useEffect(() => {
    const target = redirectPath || "/dashboard";

    if (!auth.isLoading && auth.isAuthenticated) {
      sessionStorage.removeItem(RETRY_COUNT_KEY);
      removeRedirectPath();
      try {
        LoginService.logLogin();
      } catch (err) {}
      history.replace(target);
    } else if (!auth.isLoading && !auth.isAuthenticated) {
      if (auth.error || hasAuthParams()) {
        // The callback could not be completed (error response from the identity
        // provider, or an unusable code/state). Restart the sign-in instead of
        // spinning forever; cap retries so a persistent failure can't loop.
        if (recoveryStartedRef.current || isInIframe) return;
        recoveryStartedRef.current = true;

        const retries = Number(sessionStorage.getItem(RETRY_COUNT_KEY)) || 0;
        if (retries < MAX_SIGNIN_RETRIES) {
          sessionStorage.setItem(RETRY_COUNT_KEY, String(retries + 1));
          auth.clearStaleState();
          auth.signinRedirect({ state: { returnTo: target } });
        } else {
          setSigninFailed(true);
        }
      } else {
        history.replace(target);
      }
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error]);

  const onTryAgain = () => {
    sessionStorage.removeItem(RETRY_COUNT_KEY);
    auth.clearStaleState();
    auth.signinRedirect({ state: { returnTo: redirectPath || "/dashboard" } });
  };

  if (signinFailed) {
    return (
      <div className={classes.errorContainer}>
        <p>We couldn't finish signing you in. Please try again.</p>
        <Button variant="contained" color="primary" onClick={onTryAgain}>
          Try Again
        </Button>
      </div>
    );
  }

  return <CircularProgress className={classes.spinner} />;
}

export default CallbackPage;
